import React, { useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Package, Battery, Calculator, Info, Plus, Minus, Percent, Tag, ShieldCheck, Euro } from 'lucide-react';
import { formatCurrency } from '@/lib/translations';

const UNIT_PRICE = 58500; // €58,500.00 per PowerPack
const VAT_RATE = 0.19; // 19%
const STEP = 0.5; // Allow half PowerPacks

// Discount tiers
const DISCOUNT_TIERS = [
  { minQty: 12.5, discount: 0.10, label: '10%' },
  { minQty: 7.5, discount: 0.075, label: '7.5%' },
  { minQty: 2.5, discount: 0.05, label: '5%' },
];

const getDiscount = (quantity) => {
  for (const tier of DISCOUNT_TIERS) {
    if (quantity >= tier.minQty) {
      return tier;
    }
  }
  return null;
};

export const ProductDetailsSection = ({ formData, setFormData, errors, t, language }) => {
  const locale = language === 'de' ? 'de-DE' : 'en-US';
  const p = t.productDetails;
  const isSalesAgent = formData.userRole === 'salesAgent';
  const hasCustomPrice = isSalesAgent && formData.productDetails.customUnitPrice !== null;

  const quantity = parseFloat(formData.productDetails.quantity) || 0;
  // Only apply discounts for customers (not sales agents with custom price)
  const discountTier = useMemo(() => {
    if (hasCustomPrice) return null;
    return getDiscount(quantity);
  }, [quantity, hasCustomPrice]);

  // The effective unit price
  const effectiveUnitPrice = hasCustomPrice ? formData.productDetails.customUnitPrice : UNIT_PRICE;

  // Auto-calculate prices when quantity or custom price changes
  useEffect(() => {
    const qty = parseFloat(formData.productDetails.quantity) || 0;
    const unitPrice = hasCustomPrice ? formData.productDetails.customUnitPrice : UNIT_PRICE;
    const grossPrice = qty * unitPrice;
    
    // Discounts only for customers, not for sales agent custom pricing
    const discount = hasCustomPrice ? null : getDiscount(qty);
    const discountAmount = discount ? grossPrice * discount.discount : 0;
    const netPrice = grossPrice - discountAmount;
    const vatAmount = netPrice * VAT_RATE;
    const totalPrice = netPrice + vatAmount;

    setFormData(prev => ({
      ...prev,
      productDetails: {
        ...prev.productDetails,
        unitPrice: unitPrice,
        grossPrice,
        discountPercent: discount ? discount.discount * 100 : 0,
        discountAmount,
        netPrice,
        subtotal: netPrice,
        discount: discount ? discount.discount * 100 : 0,
        vatAmount,
        vat: vatAmount,
        totalPrice
      }
    }));
  }, [formData.productDetails.quantity, formData.productDetails.customUnitPrice, hasCustomPrice, setFormData]);

  const updateQuantity = (value) => {
    let qty = parseFloat(value) || 0;
    qty = Math.round(qty * 2) / 2;
    qty = Math.max(0.5, qty);
    
    setFormData(prev => ({
      ...prev,
      productDetails: {
        ...prev.productDetails,
        quantity: qty
      }
    }));
  };

  const incrementQuantity = () => {
    const currentQty = parseFloat(formData.productDetails.quantity) || 0;
    updateQuantity(currentQty + STEP);
  };

  const decrementQuantity = () => {
    const currentQty = parseFloat(formData.productDetails.quantity) || 0;
    if (currentQty > STEP) {
      updateQuantity(currentQty - STEP);
    }
  };

  const handleCustomPriceChange = (value) => {
    const price = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      productDetails: {
        ...prev.productDetails,
        customUnitPrice: price > 0 ? price : null
      }
    }));
  };

  const enableCustomPricing = () => {
    setFormData(prev => ({
      ...prev,
      productDetails: {
        ...prev.productDetails,
        customUnitPrice: UNIT_PRICE
      }
    }));
  };

  const disableCustomPricing = () => {
    setFormData(prev => ({
      ...prev,
      productDetails: {
        ...prev.productDetails,
        customUnitPrice: null
      }
    }));
  };

  const formatQuantity = (qty) => {
    const num = parseFloat(qty);
    return num % 1 === 0 ? num.toString() : num.toFixed(1);
  };

  const getNextTierInfo = () => {
    if (hasCustomPrice) return null;
    if (quantity >= 12.5) return null;
    if (quantity >= 7.5) return { qty: 12.5, discount: '10%' };
    if (quantity >= 2.5) return { qty: 7.5, discount: '7.5%' };
    return { qty: 2.5, discount: '5%' };
  };

  const nextTier = getNextTierInfo();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Sales Agent Custom Pricing Card */}
      {isSalesAgent && (
        <Card className="border-0 shadow-md bg-amber-50/50 border-amber-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-foreground">
                {language === 'de' ? 'Vertriebsmitarbeiter-Preisanpassung' : 'Sales Agent Price Override'}
              </span>
            </div>
            
            {!hasCustomPrice ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {language === 'de' 
                    ? 'Standardpreis aktiv. Klicken Sie hier, um einen individuellen Preis festzulegen.'
                    : 'Standard pricing active. Click to set a custom unit price.'}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={enableCustomPricing}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                >
                  <Euro className="w-4 h-4 mr-1" />
                  {language === 'de' ? 'Preis anpassen' : 'Set custom price'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Label className="text-sm text-amber-700 mb-1 block">
                      {language === 'de' ? 'Individueller Stückpreis (€)' : 'Custom Unit Price (€)'}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      value={formData.productDetails.customUnitPrice || ''}
                      onChange={(e) => handleCustomPriceChange(e.target.value)}
                      className="h-12 text-lg font-semibold border-amber-300 focus:ring-amber-200"
                      placeholder="50000"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={disableCustomPricing}
                    className="text-muted-foreground hover:text-foreground mt-6"
                  >
                    {language === 'de' ? 'Zurücksetzen' : 'Reset'}
                  </Button>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-amber-100/50 border border-amber-200">
                  <Info className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    {language === 'de' 
                      ? 'Bei individuellem Preis werden Mengenrabatte deaktiviert.'
                      : 'Volume discounts are disabled when using a custom price.'}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Discount Tiers Info Card - Only show for customers or sales agents without custom price */}
      {!hasCustomPrice && (
        <Card className="border-0 shadow-md bg-success/5 border-success/20">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-5 h-5 text-success" />
              <span className="font-semibold text-foreground">
                {language === 'de' ? 'Mengenrabatte' : 'Volume Discounts'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {DISCOUNT_TIERS.slice().reverse().map((tier, index) => (
                <Badge 
                  key={index}
                  variant={quantity >= tier.minQty ? "default" : "outline"}
                  className={quantity >= tier.minQty 
                    ? "bg-success text-success-foreground" 
                    : "border-muted-foreground/30"
                  }
                >
                  {tier.minQty}+ → {tier.label} {language === 'de' ? 'Rabatt' : 'off'}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product Info Card */}
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl text-foreground">
            <Package className="w-5 h-5 text-primary" />
            {p.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Product Name */}
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm text-muted-foreground">{p.productName}</Label>
                <p className="text-lg font-semibold text-foreground">{p.productNameValue}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Battery className="w-6 h-6 text-primary" />
              </div>
            </div>
          </div>

          {/* Quantity and Unit Price */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="quantity" className="flex items-center gap-2">
                {p.quantity} <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={decrementQuantity}
                  disabled={formData.productDetails.quantity <= STEP}
                  className="h-12 w-12 shrink-0"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input
                  id="quantity"
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={formData.productDetails.quantity}
                  onChange={(e) => updateQuantity(e.target.value)}
                  placeholder={p.quantityPlaceholder}
                  className={`input-focus-ring text-lg font-medium text-center ${errors.quantity ? 'border-destructive' : ''}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={incrementQuantity}
                  className="h-12 w-12 shrink-0"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {language === 'de' 
                  ? 'Halbe PowerPacks verfügbar (z.B. 1.5, 2.5)' 
                  : 'Half PowerPacks available (e.g., 1.5, 2.5)'}
              </p>
              {errors.quantity && (
                <p className="text-xs text-destructive">{errors.quantity}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">{p.unitPrice}</Label>
              <div className={`p-3 rounded-lg border ${hasCustomPrice ? 'bg-amber-50 border-amber-200' : 'bg-muted/50 border-border'}`}>
                <p className="text-lg font-semibold text-foreground">
                  {hasCustomPrice 
                    ? formatCurrency(formData.productDetails.customUnitPrice, locale)
                    : p.unitPriceValue
                  }
                </p>
                <p className="text-xs text-muted-foreground">
                  {hasCustomPrice 
                    ? (language === 'de' ? 'Individueller Preis (ohne Rabatt)' : 'Custom price (no discount)')
                    : p.vatNote
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Next Tier Hint - Only for standard pricing */}
          {nextTier && !hasCustomPrice && (
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-2">
                <Percent className="w-4 h-4 text-primary" />
                <span className="text-sm text-muted-foreground">
                  {language === 'de' 
                    ? `Noch ${formatQuantity(nextTier.qty - quantity)} PowerPacks bis ${nextTier.discount} Rabatt!`
                    : `Only ${formatQuantity(nextTier.qty - quantity)} more PowerPacks for ${nextTier.discount} discount!`
                  }
                </span>
              </div>
            </div>
          )}

          {/* Components Info */}
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <Label className="text-sm font-medium">{p.components}</Label>
                <ul className="mt-2 space-y-1">
                  {p.componentsList.map((component, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                      {component}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Price Calculation Card */}
      <Card className="border-0 shadow-lg bg-gradient-card overflow-hidden">
        <CardHeader className="pb-4 bg-primary/5">
          <CardTitle className="flex items-center gap-2 text-xl text-foreground">
            <Calculator className="w-5 h-5 text-primary" />
            {language === 'de' ? 'Preisberechnung' : 'Price Calculation'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          {/* Gross Price (before discount) */}
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">
              {hasCustomPrice 
                ? (language === 'de' ? 'Nettobetrag' : 'Net Amount')
                : (language === 'de' ? 'Bruttopreis' : 'Gross Price')
              }
            </span>
            <span className="text-lg font-medium tabular-nums">
              {formatCurrency(formData.productDetails.grossPrice || 0, locale)}
            </span>
          </div>

          {/* Discount (if applicable - only for standard pricing) */}
          {discountTier && !hasCustomPrice && (
            <div className="flex justify-between items-center text-success">
              <span className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                {language === 'de' ? 'Mengenrabatt' : 'Volume Discount'} ({discountTier.label})
              </span>
              <span className="text-lg font-medium tabular-nums">
                -{formatCurrency(formData.productDetails.discountAmount || 0, locale)}
              </span>
            </div>
          )}
          
          {/* Net Price - only show separately if there's a discount */}
          {discountTier && !hasCustomPrice && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{p.calculations.netPrice}</span>
              <span className="text-lg font-medium tabular-nums">
                {formatCurrency(formData.productDetails.netPrice || 0, locale)}
              </span>
            </div>
          )}
          
          {/* VAT */}
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">{p.calculations.vat}</span>
            <span className="text-lg font-medium tabular-nums">
              {formatCurrency(formData.productDetails.vatAmount || 0, locale)}
            </span>
          </div>
          
          <Separator className="my-4" />
          
          {/* Total Price */}
          <div className="flex justify-between items-center p-4 rounded-lg bg-primary/10">
            <span className="text-lg font-semibold text-foreground">{p.calculations.totalPrice}</span>
            <div className="text-right">
              <span className="text-2xl font-bold text-primary tabular-nums">
                {formatCurrency(formData.productDetails.totalPrice || 0, locale)}
              </span>
              {discountTier && !hasCustomPrice && (
                <p className="text-xs text-success font-medium">
                  {language === 'de' ? 'inkl.' : 'incl.'} {discountTier.label} {language === 'de' ? 'Rabatt' : 'discount'}
                </p>
              )}
              {hasCustomPrice && (
                <p className="text-xs text-amber-600 font-medium">
                  {language === 'de' ? 'Individueller Preis' : 'Custom pricing'}
                </p>
              )}
            </div>
          </div>

          {/* Quantity Summary */}
          {formData.productDetails.quantity > 0 && (
            <p className="text-center text-sm text-muted-foreground mt-4">
              {formatQuantity(formData.productDetails.quantity)} PowerPack{formData.productDetails.quantity !== 1 ? 's' : ''} × {formatCurrency(effectiveUnitPrice, locale)}
              {discountTier && !hasCustomPrice && ` - ${discountTier.label}`} + 19% {language === 'de' ? 'MwSt' : 'VAT'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductDetailsSection;
